import React, { useMemo } from 'react';
import {
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import StatCard from '../components/StatCard';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { PeriodConfig, formatDate, formatNumber, tooltipStyle } from '../types';

interface EngagementTabProps {
  period: PeriodConfig;
}

const ENDPOINTS = ['overview', 'engagement-daily', 'engagement-hourly'];

const EngagementTab: React.FC<EngagementTabProps> = ({ period }) => {
  const { data, loading } = useAnalyticsData(ENDPOINTS, period);

  const overview = data['overview'];
  const engagement = overview?.engagement || {};

  const dailyMerged = useMemo(() => {
    const ed = data['engagement-daily'] || {};
    const allDates = Array.from(new Set([
      ...(ed.watches || []).map((d: any) => d.date),
      ...(ed.likes || []).map((d: any) => d.date),
    ])).sort();

    return allDates.map(date => {
      const findCount = (arr: any[]) => arr?.find((d: any) => d.date === date)?.count || 0;
      return {
        date: formatDate(date as string),
        watches: findCount(ed.watches),
        likes: findCount(ed.likes),
        comments: findCount(ed.comments),
        shares: findCount(ed.shares),
        saves: findCount(ed.saves),
      };
    });
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const hourlyMerged = useMemo(() => {
    const eh = data['engagement-hourly'] || {};
    const allHourlyKeys = Array.from(new Set([
      ...(eh.watches || []).map((d: any) => `${d.date}|${d.hour}`),
      ...(eh.likes || []).map((d: any) => `${d.date}|${d.hour}`),
    ])).sort();

    return allHourlyKeys.map(key => {
      const [date, hour] = (key as string).split('|');
      const findCount = (arr: any[]) => arr?.find((d: any) => d.date === date && String(d.hour) === hour)?.count || 0;
      return {
        label: `${date.slice(5)} ${hour}:00`,
        watches: findCount(eh.watches),
        likes: findCount(eh.likes),
        comments: findCount(eh.comments),
        shares: findCount(eh.shares),
        saves: findCount(eh.saves),
      };
    });
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="Watches (30d)" value={engagement.watches || 0} />
        <StatCard title="Likes (30d)" value={engagement.likes || 0} />
        <StatCard title="Comments (30d)" value={engagement.comments || 0} />
        <StatCard title="Shares (30d)" value={engagement.shares || 0} />
        <StatCard title="Saves (30d)" value={engagement.saves || 0} />
      </div>

      <ChartCard title="Daily Engagement (Last 30 Days)">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={dailyMerged}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickLine={false} interval="preserveStartEnd" />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => formatNumber(v)} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Line type="monotone" dataKey="watches" name="Watches" stroke="#FF9C15" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="likes" name="Likes" stroke="#F42026" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="comments" name="Comments" stroke="#38bdf8" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="shares" name="Shares" stroke="#34d399" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="saves" name="Saves" stroke="#c084fc" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Hourly Engagement (Last 5 Days)">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={hourlyMerged}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="label" stroke="#64748b" fontSize={9} tickLine={false} interval="preserveStartEnd" />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => formatNumber(v)} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Line type="monotone" dataKey="watches" name="Watches" stroke="#FF9C15" strokeWidth={1.5} dot={false} />
            <Line type="monotone" dataKey="likes" name="Likes" stroke="#F42026" strokeWidth={1.5} dot={false} />
            <Line type="monotone" dataKey="comments" name="Comments" stroke="#38bdf8" strokeWidth={1.5} dot={false} />
            <Line type="monotone" dataKey="shares" name="Shares" stroke="#34d399" strokeWidth={1.5} dot={false} />
            <Line type="monotone" dataKey="saves" name="Saves" stroke="#c084fc" strokeWidth={1.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
};

export default EngagementTab;
